import React, {useEffect, useMemo, useRef} from 'react';
import {useInView} from 'react-intersection-observer';
import classNames from 'classnames';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {ViewMode} from '@taopix/taopix-core';
import useViewMode from '../../Hooks/useViewMode';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {usePageActions} from '../Context/PageActions/PageActionsContext';
import {useExperience} from '../Experience/ExperienceProvider';
import PageActionButtons from './PageActionButtons';
import useKeyboardMode from '../../Hooks/useKeyboardMode';
import {useRegisterKeyboardCommand} from '../Context/KeyboardCommandsContext';

export type PageToolBarTypes = {
  pageNumber: number;
  visible?: boolean;
};

const PageToolbar = ({pageNumber, visible = true}: PageToolBarTypes) => {
  const ui = useUiState();
  const [viewMode] = useViewMode();
  const {experience} = useExperience();
  const buttons = usePageActions();
  const keyboardMode = useKeyboardMode();
  const [toolbarRef, inView] = useInView({
    rootMargin: '-200px 0px 0px 0px',
    // initialInView: true,
  });
  const pageToolbarWrapperRef = useRef<HTMLDivElement>(null);

  const firstButtonRef = useRef<HTMLElement>();

  useRegisterKeyboardCommand({
    code: 'Digit4',
    shiftKey: true,
    callback: () => {
      firstButtonRef?.current?.focus();
    },
  });

  const showPageActionButtons = useMemo(() => {
    switch (viewMode) {
      case ViewMode.Grid: {
        // there is no experience for this??????
        return true;
      }
      case ViewMode.List: {
        return experience.EDITOR.workArea.listView.pageActionButtons;
      }
      case ViewMode.Single: {
        return experience.EDITOR.workArea.singlePage.pageActionButtons;
      }
      default: {
        return false;
      }
    }
  }, [viewMode, experience]);

  useEffect(() => {
    // Get all toolbar buttons
    if (pageNumber === ui.state.pageNumber) {
      const toolbarButtons = [
        ...Array.from(pageToolbarWrapperRef.current?.querySelectorAll('button') ?? []),
      ].filter(Boolean);

      // Assign firstButtonRef
      firstButtonRef.current = toolbarButtons[0];
    }
  }, [ui.state.pageNumber, ui.state.viewMode]);

  const pageToolbarClasses = classNames(
    'flex',
    'justify-center',
    'transition-[opacity]',
    'focusMode:opacity-0', // Hide the toolbar in focus mode
    // Single Page toolbar positioning:
    viewMode === ViewMode.Single && ['sticky left-0 my-sm'],
    // List mode small screen toolbar position:
    viewMode === ViewMode.List && 'sm:mt-xxs',
    // grid view
    viewMode === ViewMode.Grid && [
      'sm:absolute',
      'sm:right-0',
      !keyboardMode && 'mouse:opacity-0', // Hide the page toolbar for mouse devices until hover
      '[:not(.dragging)&]:group-hover/grid-tile:mouse:opacity-100', // Show the page toolbar on hover unless this page is being dragged
    ],
    !inView && 'opacity-0 pointer-events-none' // Hide the page toolbar if this page is not considered to be in view
  );

  const buttonWrapperClasses = classNames(
    'flex',
    'lg:justify-center',
    'lg:flex-wrap',
    'lg:-mt-xs',
    'lg:ml-sm lg:mr-lg', // Add margin to the outer edges whilst accounting for the margin on the first button
    'sm:-ml-xxs', // Account for the margin on the first button
    viewMode !== ViewMode.Grid && 'lg:mx-lg'
  );

  const pageOptionButtonProps: Partial<ButtonProps> = {
    buttonStyle: ui.state.isSmallScreen ? 'distinct' : 'standard',
    boxless: false,
    stacked: false,
    size: 'small',
    labelAlignment: !ui.state.isSmallScreen ? 'left' : 'centre',
    hideLabel: ui.state.isSmallScreen,
    disabled: !inView && visible && ui.state.zoomLevel === 100,
    className: 'sm:ml-xxs lg:ml-sm lg:mt-xs',
  };

  if (showPageActionButtons && ui.state.zoomLevel === 100) {
    const idx = buttons.findIndex(btn => 'showMore' === btn.name);
    return (
      <>
        <div className={pageToolbarClasses} ref={toolbarRef} id={'pageToolbar'}>
          <div
            ref={pageToolbarWrapperRef}
            className={buttonWrapperClasses}
            id={`pageActionButtons-${pageNumber}`}
          >
            <PageActionButtons
              enableButtons={inView && visible && ui.state.zoomLevel === 100 && !ui.state.focusMode}
              pageNumber={pageNumber}
              buttonProps={pageOptionButtonProps}
              buttons={buttons.slice(0, -1 === idx ? buttons.length : idx + 1)}
            />
          </div>
        </div>
      </>
    );
  } else {
    return <></>;
  }
};

export default PageToolbar;
